import { IonButton, IonButtons, IonHeader, IonIcon, IonTitle, IonToolbar } from "@ionic/react";
import { chevronBackOutline } from "ionicons/icons";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef } from "react";
import EclipticSearchbar from "../../ComponentsUI/EclipticSearchbar/EclipticSearchbar";

interface ContainerProps {
    handleBack: () => void,
    onTrickSearch: (search: string) => void,
    onProfileSearch: (search: string) => void,
    onLocationSearch?: (search: string) => void,
    displayClips: boolean,
    displayRiders: boolean,
    displayMap: boolean,
    onDisplayClips: () => void,
    onDisplayRiders: () => void,
    onDisplayMap: () => void,
    searchValue: string,
}

const FilterToolbar: React.FC<ContainerProps> = (props: ContainerProps) => {
    const [showTitle, setShowTitle] = React.useState(true);

    const [clipsSelected, setClipsSelected] = React.useState(props.displayClips);
    const [defaultSearch, setDefaultSearch] = React.useState(props.searchValue);
    const [ridersSelected, setRidersSelected] = React.useState(props.displayRiders);
    const [mapSelected, setMapSelected] = React.useState(props.displayMap);

    // Track if user is actively typing to prevent external updates from overwriting input
    const isTypingRef = useRef(false);
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setShowTitle(true);
        setClipsSelected(true);
        setRidersSelected(false);
        setMapSelected(false);
    }, []);

    useEffect(() => {
        setClipsSelected(props.displayClips);
        setRidersSelected(props.displayRiders);
        setMapSelected(props.displayMap);
    }, [props.displayClips, props.displayRiders, props.displayMap]);

    // Only sync from props when user is NOT actively typing
    useEffect(() => {
        if (!isTypingRef.current) {
            setDefaultSearch(props.searchValue);
        }
    }, [props.searchValue]);

    // Debounced search function - only debounces the API call, not the UI update
    const debouncedSearch = useCallback(
        debounce(async (search: string, isClips: boolean, isRiders: boolean, isMap: boolean) => {
            if (isClips) {
                await props.onTrickSearch(search);
            } else if (isRiders) {
                await props.onProfileSearch(search);
            } else if (isMap) {
                if (props.onLocationSearch && search.trim().length > 0) {
                    await props.onLocationSearch(search);
                }
            } else {
                await props.onTrickSearch(search);
            }
        }, 300),
        [props.onTrickSearch, props.onProfileSearch, props.onLocationSearch]
    );

    const onTrickProfileSearch = (search: string) => {
        setShowTitle(false);

        // Mark user as actively typing
        isTypingRef.current = true;

        // Clear any existing timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        // Reset typing flag after user stops typing (slightly longer than debounce)
        typingTimeoutRef.current = setTimeout(() => {
            isTypingRef.current = false;
        }, 400);

        // Update UI immediately for responsive typing
        setDefaultSearch(search);
        // Debounce the actual API call
        debouncedSearch(search, clipsSelected, ridersSelected, mapSelected);
    }

    return (
        <IonHeader>
            {showTitle &&
                <IonToolbar style={{ paddingTop: `calc(0.5rem + var(--ion-safe-area-top, 0))` }} mode="ios">
                    <IonButtons slot="start" onClick={props.handleBack}>
                        <IonButton color="theme-alternative">
                            <IonIcon slot="start" icon={chevronBackOutline}></IonIcon>
                        </IonButton>
                    </IonButtons>
                    <IonTitle>The Ecliptic Lab</IonTitle>
                </IonToolbar>}
            <IonToolbar style={!showTitle ? {
                paddingTop: `calc(0.5rem + var(--ion-safe-area-top, 0))`,
                marginTop: "0rem",
                marginBottom: "-0.75rem",
                paddingBottom: "0rem"
            } : null} mode="ios">
                {!showTitle &&
                    <IonButtons slot="start" onClick={props.handleBack}>
                        <IonButton color="theme-alternative">
                            <IonIcon slot="start" icon={chevronBackOutline}></IonIcon>
                        </IonButton>
                    </IonButtons>
                }
                {
                    <EclipticSearchbar
                        placeholder={mapSelected ? "Search for locations and places" : "Search for tricks, riders, spots, and more"}
                        animated={false}
                        value={defaultSearch}
                        showClearButton="focus"
                        showCancelButton="focus"
                        inputmode="search"
                        onIonInput={(e) => {
                            onTrickProfileSearch(e.detail.value)
                        }}
                        onFocus={() => {
                            setShowTitle(false)
                        }}
                        onIonCancel={() => {
                            setShowTitle(true)
                        }}
                        debounce={0}
                    />
                }
            </IonToolbar>
            {
                !showTitle &&
                <IonToolbar style={{
                    paddingLeft: `3.4rem`,
                    paddingRight: `3rem`,
                    marginTop: "0rem",
                    marginBottom: "0rem",
                    paddingTop: "0rem",
                    paddingBottom: "0rem"
                }} mode="ios">
                    <IonButtons className="flex flex-row gap-x-4">
                        <IonButton color={!clipsSelected ? "theme-secondary" : "favorite"}
                            fill={!clipsSelected ? "clear" : "solid"} shape="round"
                            onClick={props.onDisplayClips}>
                            Clips
                        </IonButton>
                        <IonButton color={!ridersSelected ? "theme-secondary" : "favorite"}
                            fill={!ridersSelected ? "clear" : "solid"} shape="round"
                            onClick={props.onDisplayRiders}>
                            Riders
                        </IonButton>
                        <IonButton color={!mapSelected ? "theme-secondary" : "favorite"}
                            fill={!mapSelected ? "clear" : "solid"} shape="round"
                            onClick={props.onDisplayMap}>
                            Map
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            }
        </IonHeader>
    )
};

export default FilterToolbar;